import { buildApiUrl } from './api';

export type Subscription = {
    isActive: boolean;
    autoRenew?: boolean;
    type?: string;
    code?: string | null;
    amount?: number | null;
    promoCodeUsagePeriods?: number | null;
    isTrial?: boolean;
    activeUntil?: string;
    paymentUrl?: string;
    isCancelable: boolean | undefined;
    isOverdue: boolean | undefined;
    cardInfo: {
        lastFourDigits: number | null;
        type: string | null;
    } | null;
};

export type SubscriptionType = 'TRIAL' | 'MTH' | 'YEAR' | 'TEST' | 'GIFT' | 'LFT';
type ErrorResponse = {
    code: string;
};

type FaultResponse = {
    message: string;
    errors: ErrorResponse[];
};

type SuccessfulGetBody = {
    subscription: Subscription;
};

type SuccessfulCreateBody = {
    subscription: Subscription;
};

type BadRequestBody = FaultResponse;

type SubscriptionServiceGetResponse = Omit<Response, 'json'> & {
    status: 200;
    json: () => SuccessfulGetBody | PromiseLike<SuccessfulGetBody>;
};

type SubscriptionServiceCreateResponse =
    | (Omit<Response, 'json'> & {
        status: 200;
        json: () => SuccessfulCreateBody | PromiseLike<SuccessfulCreateBody>;
    })
    | (Omit<Response, 'json'> & {
        status: 400;
        json: () => BadRequestBody | PromiseLike<BadRequestBody>;
    });

export type SubscriptionServiceCreateRequest = {
    type: SubscriptionType;
    code?: string | null;
};

export type SubscriptionServiceCreatePromoRequest = {
    code: string;
};

type SuccessfulCancelBody = {
    status: 'ok';
};

type SubscriptionServiceCancelResponse =
    | (Omit<Response, 'json'> & {
        status: 200;
        json: () => SuccessfulCancelBody | PromiseLike<SuccessfulCancelBody>;
    })
    | (Omit<Response, 'json'> & {
        status: 400;
        json: () => BadRequestBody | PromiseLike<BadRequestBody>;
    });

type PromoSubscriptionResponse = {
    subscription: {
        isActive: boolean;
        subscription: Subscription;
    };
};
type PromoSubscriptionServiceCreateResponse =
    | (Omit<Response, 'json'> & {
        status: 200;
        json: () => PromoSubscriptionResponse | PromiseLike<PromoSubscriptionResponse>;
    })
    | (Omit<Response, 'json'> & {
        status: 400;
        json: () => BadRequestBody | PromiseLike<BadRequestBody>;
    });

class SubscriptionService {
    static async get() {
        const response = (await fetch(buildApiUrl('/subscription'), {
            method: 'GET'
        })) as SubscriptionServiceGetResponse;

        if (response.status === 200) {
            return response.json();
        }

        throw Error('Unhandled response code');
    }

    static async create(data: SubscriptionServiceCreateRequest) {
        const response = (await fetch(buildApiUrl('/subscription/create'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })) as SubscriptionServiceCreateResponse;

        if (response.status === 200 || response.status === 400) {
            return response.json();
        }

        throw Error('Unhandled response code');
    }

    static async createPromo(data: SubscriptionServiceCreatePromoRequest) {
        const response = (await fetch(buildApiUrl('/subscription/create-promo'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })) as PromoSubscriptionServiceCreateResponse;

        if (response.status === 200 || response.status === 400) {
            return response.json() as any;
        }

        throw Error('Unhandled response code');
    }

    static async cancel() {
        const response = (await fetch(buildApiUrl('/subscription/cancel'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        })) as SubscriptionServiceCancelResponse;

        if (response.status === 200 || response.status === 400) {
            return response.json();
        }

        throw Error('Unhandled response code');
    }

    static async restart() {
        const response = (await fetch(buildApiUrl('/subscription/restart'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        })) as SubscriptionServiceCancelResponse;

        if (response.status === 200 || response.status === 400) {
            return response.json();
        }

        throw Error('Unhandled response code');
    }

    static async update(data: SubscriptionServiceCreateRequest) {
        const response = (await fetch(buildApiUrl('/subscription/update'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })) as SubscriptionServiceCreateResponse;

        if (response.status === 200 || response.status === 400) {
            return response.json();
        }

        throw Error('Unhandled response code');
    }

    // Не знаю, надо ли создавать ради этого целый новый класс PaymentService
    static async updateCard() {
        const response = (await fetch(buildApiUrl('/payment/update-card'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        })) as SubscriptionServiceCreateResponse;

        if (response.status === 200 || response.status === 400) {
            return response.json();
        }

        throw Error('Unhandled response code');
    }
}

export default SubscriptionService;
